import { useEffect, useRef } from 'react';

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = heroRef.current?.querySelectorAll('.fade-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="hero" className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/3f7186ef-158a-4704-a75d-2965a55f3c17/441a46ae-2841-4ef1-9f5c-de1db2b930fd_BACKGROUND-HERO.png?v=f14dbd6202f801ad28be3288767e38c3"
          alt="Projeto de interiores INTRÊ Arquitetura"
          className="w-full h-full object-cover object-center"
          style={{ imageRendering: 'auto' }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#301531]/85 via-[#301531]/60 to-[#301531]/30" />
      </div>

      {/* Content */}
      <div ref={heroRef} className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-16">
        <div className="max-w-2xl">
          <div
            className="fade-up opacity-0 translate-y-6 transition-all duration-700 delay-100"
            style={{ transitionProperty: 'opacity, transform' }}
          >
            <span className="inline-block px-4 py-1.5 border border-[#D6C4D6]/60 text-[#D6C4D6] font-montserrat text-xs font-medium tracking-[0.2em] uppercase rounded-full mb-6">
              Arquitetura de Interiores · São Paulo
            </span>
          </div>

          <h1
            className="font-gothic text-white leading-tight mb-6 fade-up opacity-0 translate-y-6 transition-all duration-700 delay-200"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', transitionProperty: 'opacity, transform' }}
          >
            Seu apartamento planejado com{' '}
            <span className="text-[#D6C4D6]">estratégia</span>,{' '}
            <span className="text-[#D6C4D6]">estética</span> e{' '}
            <span className="text-[#D6C4D6]">funcionalidade</span>
          </h1>

          <p
            className="font-montserrat text-[#D6C4D6] text-base md:text-lg leading-relaxed mb-10 max-w-xl fade-up opacity-0 translate-y-6 transition-all duration-700 delay-300"
            style={{ transitionProperty: 'opacity, transform' }}
          >
            Projetos de interiores pensados para transformar espaços e simplificar sua obra
          </p>

          <div
            className="flex flex-col sm:flex-row gap-4 fade-up opacity-0 translate-y-6 transition-all duration-700 delay-500"
            style={{ transitionProperty: 'opacity, transform' }}
          >
            <a
              href="https://wa.me/5511930672567"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#301531] font-montserrat font-bold text-sm rounded-full hover:bg-[#D6C4D6] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg" />
              Solicitar atendimento
            </a>
            <a
              href="#portfolio"
              onClick={(e) => { e.preventDefault(); document.querySelector('#portfolio')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/50 text-white font-montserrat font-medium text-sm rounded-full hover:border-[#D6C4D6] hover:text-[#D6C4D6] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Conheça nosso trabalho
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="font-montserrat text-white/50 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-white/50 text-lg" />
      </div>

      <style>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}
